/**
 * A host's request for a folder on the person's paired computer, and its run on it (ADR 0106).
 *
 * The decisions the frame makes about it, as pure functions a test can reach, beside the picker
 * that draws them (`folder-picker.tsx`) and the frame app that wires both to the bridge
 * (`apps/web/src/main.tsx`): whether a folder is the repository the host named, where the picker
 * opens, what closing it without a choice tells the host, and how a `start-work` `code` becomes a
 * folder attached to the session before its first turn, or an error and no turn at all.
 *
 * @module chat42/react/host-folder
 */

import { folderId } from '@chat42/runtime/workspace';

/** What the host said the folder is for. `PickFolder['hint']` in `@chat42/embed`. */
export interface FolderHint {
  repository?: string;
  label?: string;
}

/** A folder the person chose, as `chat42:folder-picked` carries it, less the request id. */
export interface PickedFolder {
  deviceId: string;
  deviceName: string;
  path: string;
  remote?: string;
  branch?: string;
  /** Only where the hint named a repository. */
  matches?: boolean;
}

/** How the picker closed. The frame turns each into one of the three answers. */
export type FolderPickOutcome =
  | { kind: 'picked'; folder: PickedFolder }
  | { kind: 'cancelled' }
  | { kind: 'failed'; case: 'no-machine' | 'offline' | 'not-allowed' | 'other' };

/**
 * A repository address reduced to its host and path, for comparing two of them.
 *
 * The same reduction a host makes of its own (Rule42's `repositoryKey`): the scheme, any user and
 * password, a `git@host:` form, a trailing `.git` and slashes are set aside, and case does not
 * count. So `git@github.com:Acme/App.git` and `https://github.com/acme/app` are one repository,
 * which they are.
 */
export function repositoryKey(address: string): string {
  return address.trim().toLowerCase()
    .replace(/^git\+/, '')
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/^[^@/]+@/, '')
    .replace(/^([^/:]+):(?!\d+\/)/, '$1/')
    .replace(/^([^/:]+):\d+\//, '$1/')
    .replace(/\/+$/, '')
    .replace(/\.git$/, '')
    .replace(/\/+$/, '');
}

/** Whether a folder's remote is the repository the host named. Undefined where nothing was named. */
export function remoteMatches(remote: string | undefined, repository: string | undefined): boolean | undefined {
  if (!repository?.trim()) return undefined;
  if (!remote?.trim()) return false;
  return repositoryKey(remote) === repositoryKey(repository);
}

/**
 * The key a chosen folder is remembered under: the person, then what the host said it is for.
 *
 * Per person because a frame on a shared computer is several people's, and a folder is somebody's
 * own. Per hint because a host asks for different folders for different things: the repository
 * when it named one, its label when it did not, and otherwise one place for everything.
 */
export function folderMemoryKey(userId: string, hint?: FolderHint): string {
  const what = hint?.repository ? `repo:${repositoryKey(hint.repository)}` : hint?.label ? `label:${hint.label.trim()}` : 'any';
  return `chat42:folder-pick:${userId}:${what}`;
}

/** The folder last chosen for this hint by this person, where this browser kept one. */
export function recallFolder(
  storage: Pick<Storage, 'getItem'> | undefined, userId: string | undefined, hint?: FolderHint,
): { deviceId: string; path: string } | undefined {
  // Nobody to remember it for: nothing is recalled rather than somebody else's folder.
  if (!storage || !userId) return undefined;
  try {
    const raw = storage.getItem(folderMemoryKey(userId, hint));
    const v = raw ? (JSON.parse(raw) as { deviceId?: unknown; path?: unknown }) : undefined;
    return typeof v?.deviceId === 'string' && typeof v.path === 'string' ? { deviceId: v.deviceId, path: v.path } : undefined;
  } catch {
    return undefined;
  }
}

/** Keep the folder chosen, so the picker opens there next time. Never throws: it is a nicety. */
export function rememberFolder(
  storage: Pick<Storage, 'setItem'> | undefined, userId: string | undefined, hint: FolderHint | undefined,
  folder: { deviceId: string; path: string },
): void {
  if (!storage || !userId) return;
  try {
    storage.setItem(folderMemoryKey(userId, hint), JSON.stringify({ deviceId: folder.deviceId, path: folder.path }));
  } catch { /* storage full or refused: the next pick starts at the list of machines */ }
}

/**
 * What closing the picker without a choice tells the host.
 *
 * Cancelled, where there was something to choose and the person chose not to. Failed, where
 * there was not: no machine at all, none online, or the frame refused the one they tried. A host
 * can then say what to do (pair one, wake it) rather than treat it as a change of mind.
 */
export function pickClosed(state: {
  machines: number; online: number; refused?: boolean; failed?: boolean;
}): FolderPickOutcome {
  if (state.refused) return { kind: 'failed', case: 'not-allowed' };
  if (state.failed) return { kind: 'failed', case: 'other' };
  if (state.machines === 0) return { kind: 'failed', case: 'no-machine' };
  if (state.online === 0) return { kind: 'failed', case: 'offline' };
  return { kind: 'cancelled' };
}

/** Whether a machine's refusal is a refusal (not this person's, not permitted) rather than trouble. */
export const refusedByMachine = (message: string): boolean =>
  /\b(403|forbidden|not allowed|not permitted|no such machine|not yours|permission)\b/i.test(message);

/**
 * Where "Pair this computer" goes: this app's own Machines screen, in a tab of its own.
 *
 * A query rather than a route, because the app has no routes: the settings dialog reads it once
 * on arrival (`settingsFromAddress`) and the address is put back.
 */
export const PAIR_PATH = '/?settings=machines';

/** A settings room named in the page's address, for the app to open on arrival. */
export function settingsFromAddress(search: string): 'machines' | undefined {
  // One room, the one a frame sends people to. A link that could open any room would be a door
  // for any page to put somebody in front of a screen of its choosing.
  return new URLSearchParams(search).get('settings') === 'machines' ? 'machines' : undefined;
}

/** The code a host's `start-work` names. `StartWork['code']` in `@chat42/embed`. */
export interface HostCode {
  deviceId: string;
  path: string;
  readOnly?: boolean;
}

/** What attaching a host's code needs of a client. `Chat42Client` is one; a test's fake is another. */
export interface HostCodeClient {
  loadDevices(): Promise<void>;
  readonly state: { devices: ReadonlyArray<{ id: string; label: string; connected: boolean; revokedAt?: string }> };
  checkWorkspace(input: { workspaceId: string }): Promise<{ ok: boolean; case?: string; said?: string; next?: string }>;
  attachWorkspace(sessionId: string, workspaceId: string, opts?: { readOnly?: boolean; filesReadOnly?: boolean }): Promise<void>;
  addWorkspaces(sessionId: string, workspaceIds: string[], opts?: { readOnly?: boolean; filesReadOnly?: boolean }): Promise<void>;
}

/** Why a host's code was not attached, in the cases `chat42:error` carries. */
export type HostCodeTrouble = { case: string; message: string };

/**
 * Attach the folder a host's `start-work` names to the session, before its first turn.
 *
 * Asked in order, and stopped at the first no, so a turn never starts without the code:
 *
 * 1. **The machine is the person's, and online.** From the person's own list, which only ever
 *    holds their machines. Offline is `machine-offline`, said before anything else is asked.
 * 2. **The folder is there.** The same check a Where choice is given (`/api/workspaces/check`):
 *    `folder-gone` where it is not, and the machine asleep since the list was read is
 *    `machine-offline` too.
 * 3. **Attached**, beside what the session has where it already has code on that machine, or as
 *    its first. Read-only where the host asked: the git door refuses a push and the session's
 *    tools refuse every write and every command in it (ADR 0106).
 *
 * Returns the trouble rather than throwing, for the frame to say to the host as `chat42:error`.
 */
export async function attachHostCode(
  client: HostCodeClient,
  session: { id: string; tier?: number; deviceId?: string },
  code: HostCode,
): Promise<{ ok: true } | ({ ok: false } & HostCodeTrouble)> {
  const name = code.path.split('/').filter(Boolean).pop() ?? code.path;
  await client.loadDevices().catch(() => undefined);
  const device = client.state.devices.find((d) => d.id === code.deviceId && !d.revokedAt);
  if (!device) {
    return { ok: false, case: 'not-yours', message: `The computer that has ${name} is not one of yours, so it was not opened. Nothing was started.` };
  }
  if (!device.connected) {
    return {
      ok: false, case: 'machine-offline',
      message: `${device.label} is not online, so ${name} could not be opened. Start it, then try again. Nothing was started.`,
    };
  }

  const workspaceId = folderId(code.deviceId, code.path);
  let check: Awaited<ReturnType<HostCodeClient['checkWorkspace']>>;
  try {
    check = await client.checkWorkspace({ workspaceId });
  } catch (err) {
    return { ok: false, case: 'other', message: `${name} could not be checked: ${(err as Error).message} Nothing was started.` };
  }
  if (!check.ok) {
    const said = [check.said, check.next].filter(Boolean).join(' ');
    const which = check.case === 'machine-asleep' ? 'machine-offline' : check.case ?? 'other';
    return { ok: false, case: which, message: `${said || `${name} could not be opened.`} Nothing was started.` };
  }

  const opts = code.readOnly ? { readOnly: true, filesReadOnly: true } : {};
  try {
    // Beside the code it has where it is on that machine; a session with code anywhere else is
    // refused by the runtime as two places at once, which is the right answer and says so.
    if ((session.tier ?? 0) >= 1) await client.addWorkspaces(session.id, [workspaceId], opts);
    else await client.attachWorkspace(session.id, workspaceId, opts);
  } catch (err) {
    const message = (err as Error).message;
    const which = /not connected/i.test(message)
      ? 'machine-offline'
      : /no longer there|No such folder/i.test(message) ? 'folder-gone' : 'other';
    return { ok: false, case: which, message: `${name} was not opened: ${message} Nothing was started.` };
  }
  return { ok: true };
}
